"use client";

import { SCartDto, SCartItemDto } from "@/__generated__/sdk";
import { Backdrop, Button, Card, CircularProgress, Divider, Grid, Icon, IconButton, Link, Popover, Stack, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Image from "next/image";
import { Add, Close, Remove } from "@mui/icons-material";
import { useAppDispatch } from "@/redux/hooks";
import { addOrUpdateItemInCart } from "@/redux/features/cart/cartSlice";
import { useSnackbar } from "notistack";
import React, { useRef } from "react";
import AddIcon from "@/app/_components/Icons/AddIcon";
import TrashIcon from "@/app/_components/Icons/TrashIcon";
import MinusIcon from "@/app/_components/Icons/MinusIcon";
import CartItemHistory from "../cart/components/CartItemHistory";
import { SCartItemChangeHistoryDto } from "@/__generated__/graphql";

type Props = {
  item: SCartItemDto;
};

export default function CartItemCard({ item }: Props) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  // Create a ref for the delete button
  const deleteButtonRef = useRef<HTMLButtonElement | null>(null);

  // Open the popover programmatically by setting anchorEl to the ref
  // const openPopoverProgrammatically = () => {
  //   if (deleteButtonRef.current) {
  //     setAnchorEl(deleteButtonRef.current);
  //   }
  // };

  // const handleDeleteItemClick = (event: React.MouseEvent<HTMLButtonElement>) => {
  //   setAnchorEl(event.currentTarget);
  // };

  const handleDeleteItemClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? `item-${item.id}-delete-popover` : undefined;

  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const maxBasketCount = item.offer ? item.offer.maxBasketCount : item.variant.maxBasketCount;

  const handleAddClick = () => {
    const q = item.quantity + 1;
    if (q > maxBasketCount) {
      enqueueSnackbar({ message: "موجودی کافی نیست", variant: "warning" });
      return;
    }
    dispatch(
      addOrUpdateItemInCart({
        createCartInput: {
          productId: item.product.id,
          variantId: item.variant.id,
          quantity: item.quantity + 1,
        },
      }),
    );
  };

  const handleRemoveClick = () => {
    const q = item.quantity - 1;
    dispatch(
      addOrUpdateItemInCart({
        createCartInput: {
          productId: item.product.id,
          variantId: item.variant.id,
          quantity: q,
        },
      }),
    );
  };

  const handleDeleteClick = () => {
    handleDeleteItemClose();
    dispatch(
      addOrUpdateItemInCart({
        createCartInput: {
          productId: item.product.id,
          variantId: item.variant.id,
          quantity: 0,
        },
      }),
    );
  };

  const productLink = `/products/${item.product.code}/${item.product.title.replaceAll(" ", "-")}`;
  const disableAdd = +maxBasketCount === +item.quantity;
  const history: SCartItemChangeHistoryDto | null | undefined = item.changeHistories?.[0];
  return (
    <Stack
      sx={{
        ...(item.quantity < 1 && { backgroundColor: "error.light" }),
        borderRadius: 3,
      }}
      spacing={1}
    >
      <Stack direction={"row"} spacing={1}>
        {item.product.mainImage && (
          <Link target="_blank" href={productLink}>
            <Box
              sx={{
                border: 1,
                borderRadius: 2,
                borderColor: "grey.200",
                position: "relative",
                width: 65,
                aspectRatio: 1,
              }}
            >
              <Image
                src={item.product.mainImage.address}
                fill
                alt={item.product.mainImage.alt || item.product.title}
                style={{ objectFit: "cover", borderRadius: 2 * 4 }}
              />
            </Box>
          </Link>
        )}
        <Link color="inherit" underline="none" target="_blank" href={productLink}>
          <Stack sx={{ height: "100%" }} justifyContent="space-between">
            <Typography
              variant="subtitle1"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {item.product.title}
            </Typography>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Typography variant="body2" component="span" sx={{ color: "grey.600" }} lineHeight={1.5}>
                {item.variant.color.title}
              </Typography>
              {item.variant.size && (
                <>
                  <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5 }} />
                  <Typography variant="body2" sx={{ color: "grey.600" }} component="span" lineHeight={1.5}>
                    {item.variant.size}
                  </Typography>
                </>
              )}
              {item.variant.material && (
                <>
                  <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5 }} />
                  <Typography variant="body2" sx={{ color: "grey.600" }} component="span" lineHeight={1.5}>
                    {item.variant.material}
                  </Typography>
                </>
              )}
            </Stack>
            <CartItemHistory dense history={history} />
          </Stack>
        </Link>
      </Stack>
      <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
        {item.quantity > 0 ? (
          <Stack direction={"row"} alignItems={"center"} spacing={1}>
            <Box sx={{ backgroundColor: "grey.50", p: 0.5, borderRadius: 800 }} display="flex" alignItems="center" gap={1}>
              <Button
                variant="contained"
                disableElevation
                sx={{ borderRadius: 100, minWidth: "auto", p: 0.4 }}
                onClick={handleAddClick}
                disabled={disableAdd}
              >
                <Icon fontSize="small">
                  <AddIcon />
                </Icon>
              </Button>
              <Typography variant="body2" textAlign={"center"} minWidth={15}>
                {item.quantity}
              </Typography>
              <Button variant="contained" disableElevation sx={{ borderRadius: 100, minWidth: "auto", p: 0.4 }} onClick={handleRemoveClick}>
                {item.quantity <= 1 ? (
                  <Icon fontSize="small">
                    <TrashIcon />
                  </Icon>
                ) : (
                  <Icon fontSize="small">
                    <MinusIcon />
                  </Icon>
                )}
              </Button>
            </Box>
            {item.quantity > 1 && (
              <IconButton color="error" onClick={handleDeleteClick}>
                <Icon fontSize="small">
                  <TrashIcon />
                </Icon>
              </IconButton>
            )}
          </Stack>
        ) : (
          <Button
            variant="contained"
            disableElevation
            color="error"
            fullWidth
            size="small"
            sx={{ borderRadius: 100 }}
            onClick={handleDeleteClick}
            startIcon={
              <Icon fontSize="small">
                <TrashIcon />
              </Icon>
            }
          >
            حذف
          </Button>
        )}
        {item.quantity > 0 && (
          <Stack direction={"row"} mr={0.4} alignItems={"end"}>
            {item.offer ? (
              <Stack alignItems={"end"}>
                <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={1}>
                  <Typography variant="body2" component={"s"} color={"grey.400"}>
                    {item.variant.price.toLocaleString()} تومان
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: "error.100",
                      borderRadius: 4,
                      color: "white.main",
                      height: 18,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography sx={{ mx: 1 }} variant="caption" lineHeight={0}>
                      % {item.offer.percentage}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction={"row"} alignItems={"center"} spacing={0.3}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {item.offer.price.toLocaleString()}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: "grey.500" }}>
                    تومان
                  </Typography>
                </Stack>
              </Stack>
            ) : (
              <Stack direction={"row"} alignItems={"center"} spacing={0.3}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {item.variant.price.toLocaleString()}
                </Typography>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: "grey.500" }}>
                  تومان
                </Typography>
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
